import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { Provider, EnvironmentProviders, ENVIRONMENT_INITIALIZER, inject, APP_INITIALIZER } from "@angular/core"
import { MockDataInterceptor } from "@template-ui/interceptors/data.interceptor";
import { EzFormDialogService, EzTextService } from "@template-ui/services";
import { RouteLowercaseService } from "@template-ui/services/route-lowercase.service";
import { DROPDOWN_SERVICE_TOKEN } from "@template-ui/tokens";
import { provideMarkdown } from "ngx-markdown";
import { provideDropdownRegistry } from "./dropdown-config.provider";
import { USER_SETTINGS_TOKEN } from "@template-ui/types";
import { UI_GRID_APP_COLUMN_TYPES } from "@template-ui/tokens/grid.tokens";
import { AppGridColumnTypes } from "@app/shared/components/tables/ag-grid-cells/app-grid-column-types";
import { DropdownOptionsService } from "@app/shared/services/dropdown-options.service";
import { SettingsService } from "@app/shared/services/settings.service";
import { APP_SHOWCASE_SAMPLE_COMPONENTS_MAP } from "@app/shared/showcase/showcase-components.gen";

/**
 * The RouteLowercaseService will convert the specified route segments to lowercase
 * to match the 'normal' Angular routes which are usually lowercase. This is useful
 * for using Typecodes from the database which are typically UPPERCASE in routerLinks
 * to route to lowercase routes.
 * NOTE: Although this still works, I found a better way by implementing formatting options in the Text Template service
 * IF this gives you problems, feel free to rip it out.
 * TODO: Put this in the template-ui or remove it
 * @param service
 * @returns
 */
function initializeRouteLowercase(service: RouteLowercaseService) {
  return () => {
    service.setRouteParamsToLowercase(['detail', 'commutation', 'investigation']);
  };
}

/**
 * Register date formatters to keep text-template date rendering consistent.
 */
function registerAppDateFormatter(ezText: EzTextService) {
  return () => {
    ezText.registerFormatter('appDate', {
      dateFormat: 'MM/dd/yyyy'  // <-- Do NOT use uppercase D or uppercase Y.
    });
    // Deprecated compatibility alias for saved text templates.
    ezText.registerFormatter('bitsDate', {
      dateFormat: 'MM/dd/yyyy'  // <-- Do NOT use uppercase D or uppercase Y.
    });
  };
}

function registerAppDateTimeFormatter(ezText: EzTextService) {
  return () => {
    ezText.registerFormatter('appDateTime', {
      dateFormat: 'MM/dd/yyyy hh:mm a'  // <-- Do NOT use uppercase D or uppercase Y.
    });
    // Deprecated compatibility alias for saved text templates.
    ezText.registerFormatter('bitsDateTime', {
      dateFormat: 'MM/dd/yyyy hh:mm a'  // <-- Do NOT use uppercase D or uppercase Y.
    });
  };
}

/**
 * Consolidate all template-ui provider stuff in here
 */
export const provideTemplateUi = (): Array<Provider | EnvironmentProviders> => {
  const providers: Array<Provider | EnvironmentProviders> = [

    // Automatically lowercase some route segments to match lowercase routes
    // NOTE: Although this still works, I found a better way by implementing formatting options in the Text Template service
    { provide: APP_INITIALIZER, useFactory: initializeRouteLowercase, deps: [RouteLowercaseService], multi: true },

    // Allow mocking API calls. Placed AFTER provideBits so we still get the loading thing (doesn't work)
    { provide: HTTP_INTERCEPTORS, useClass: MockDataInterceptor, multi: true },

    // Instantiate the form dialog service so that it doesn't need to be
    // imported by anything else which might cause circular dependencies.
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useFactory: () => {
        inject(EzFormDialogService); // instantiate; effect subscribes to dialogRequest
        return () => { };
      },
    },

    // Provide the mapping for the application showcase samples
    {
      provide: APP_SHOWCASE_SAMPLE_COMPONENTS_MAP,
      useValue: APP_SHOWCASE_SAMPLE_COMPONENTS_MAP
    },

    // This was how I decoupled the dropdown service from BITS. I'd like for this to not have to exist.
    {
      provide: DROPDOWN_SERVICE_TOKEN,
      useClass: DropdownOptionsService
    },

    // Provide a setting service if you want template-ui to be able to save/retrieve user settings.
    { provide: USER_SETTINGS_TOKEN, useExisting: SettingsService },

    // Provide app-specific column type for use by the template-ui
    { provide: UI_GRID_APP_COLUMN_TYPES, useValue: AppGridColumnTypes.BuiltInColumnTypes },

    // Done here because there's stuff in template-ui that depends on it.
    provideMarkdown(),  // For now, we are using defaults. If necessary, you can add options here.

    // Provide date formatters in one place for app text templates.
    {
      provide: APP_INITIALIZER,
      useFactory: registerAppDateFormatter,
      deps: [EzTextService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: registerAppDateTimeFormatter,
      deps: [EzTextService],
      multi: true
    },

    provideDropdownRegistry()

  ];

  return providers;
}
